import React from "react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { Trash2, MessageSquareHeart } from "lucide-react";

const STATUS_STYLES = {
  pending: "bg-[#FFB6C1]/10 text-[#FFB6C1] border-[#FFB6C1]/30",
  confirmed: "bg-[#FF2079]/10 text-[#FF2079] border-[#FF2079]/40",
  completed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  cancelled: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30",
};

const STATUSES = ["pending", "confirmed", "completed", "cancelled"];

function StatusSelect({ booking, onChange }) {
  return (
    <Select value={booking.status} onValueChange={(v) => onChange(booking.id, v)}>
      <SelectTrigger
        data-testid={`status-select-${booking.id}`}
        className={`w-[130px] h-8 text-[10px] tracking-[0.2em] uppercase rounded-sm border focus:ring-0 ${STATUS_STYLES[booking.status]}`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-[#141414] border-white/10 text-white">
        {STATUSES.map((s) => (
          <SelectItem key={s} value={s} className="focus:bg-[#FF2079]/20 focus:text-white capitalize">
            {s}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function DeleteBookingDialog({ booking, onDelete }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          data-testid={`delete-booking-${booking.id}-btn`}
          size="sm"
          variant="ghost"
          className="h-8 w-8 p-0 text-[#A09CA3] hover:text-red-400 hover:bg-red-500/10"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-[#141414] border-white/10 text-white">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display text-2xl">Remove reservation?</AlertDialogTitle>
          <AlertDialogDescription className="text-[#A09CA3]">
            This will permanently delete the booking for {booking.name}. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-transparent border-white/10 text-white hover:bg-white/5">
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid={`confirm-delete-${booking.id}-btn`}
            onClick={() => onDelete(booking.id)}
            className="bg-red-500 hover:bg-red-600 text-white"
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function AskReviewButton({ booking, reviewLink }) {
  if (booking.status !== "completed" || !reviewLink) return null;
  const msg = `Hi ${booking.name}, thank you for visiting Pink Glow Studio! If you loved your ${booking.service_name}, we'd be so grateful for a quick Google review: ${reviewLink}`;
  return (
    <a
      href={`sms:${booking.phone}?&body=${encodeURIComponent(msg)}`}
      data-testid={`ask-review-${booking.id}-btn`}
      title="Text this client a Google review link"
      className="inline-flex items-center h-8 px-2 rounded-sm text-[#A09CA3] hover:text-[#FF2079] hover:bg-[#FF2079]/10"
    >
      <MessageSquareHeart className="w-3.5 h-3.5" />
    </a>
  );
}

function BookingRow({ booking, onStatus, onDelete, reviewLink }) {
  return (
    <TableRow data-testid={`booking-row-${booking.id}`} className="border-white/5 hover:bg-white/[0.02]">
      <TableCell className="py-4">
        <div className="text-white">{booking.name}</div>
        <div className="text-xs text-[#A09CA3]">{booking.phone}</div>
        {booking.email && <div className="text-xs text-[#A09CA3]">{booking.email}</div>}
      </TableCell>
      <TableCell>
        <div className="text-white">{booking.service_name}</div>
        <div className="text-xs text-[#FFB6C1]">{booking.service_category}</div>
      </TableCell>
      <TableCell>
        <div className="text-white">{booking.date}</div>
        <div className="text-xs text-[#A09CA3]">{booking.time}</div>
      </TableCell>
      <TableCell className="text-white">{booking.service_price_label || `$${booking.service_price}`}</TableCell>
      <TableCell>
        <StatusSelect booking={booking} onChange={onStatus} />
      </TableCell>
      <TableCell className="text-right">
        <AskReviewButton booking={booking} reviewLink={reviewLink} />
        <DeleteBookingDialog booking={booking} onDelete={onDelete} />
      </TableCell>
    </TableRow>
  );
}

export default function BookingsTable({ loading, bookings, onStatus, onDelete, reviewLink }) {
  if (loading) {
    return <div className="p-10 text-center text-[#A09CA3] text-sm">Loading reservations…</div>;
  }
  if (bookings.length === 0) {
    return (
      <div className="p-10 text-center text-[#A09CA3] text-sm" data-testid="empty-bookings">
        No reservations yet.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="border-white/5 hover:bg-transparent">
            {["Guest", "Service", "Date · Time", "Price", "Status"].map((h) => (
              <TableHead key={h} className="text-[10px] tracking-[0.25em] uppercase text-[#A09CA3]">{h}</TableHead>
            ))}
            <TableHead className="text-right text-[10px] tracking-[0.25em] uppercase text-[#A09CA3]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {bookings.map((b) => (
            <BookingRow key={b.id} booking={b} onStatus={onStatus} onDelete={onDelete} reviewLink={reviewLink} />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
